import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { TeamInboxMessagesCacheReducerActions } from '@common-reducers/DBServiceThunks';
import {
    waDeleteMessageTool,
    waPinMessageTool,
    waReactToMessageThunkTool,
    waStarMessageTool,
    waUnpinMessageTool,
} from '@/extension/context/content/thunks/WhatsappThunks';
import { WA_TOOL_COMMANDS } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import { selectServedCommands } from '@common/reducers/ConnectedEnginesSlice';
import { createNoteFromMessageThunk, createTaskFromMessageThunk } from '@common/thunks/MessageActionsThunks';
import { openEditor } from '@common/reducers/RemindersUIReducer';
import { selectCurrentWaChat } from './redux/TeamInboxSelectors';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { formatPhoneNumber, mediaTypeLabel, toggleMyReaction } from './team-inbox-helpers';

export interface MessageActions {
    /** Text used for note/task/reminder payloads (body/caption or media label). */
    messageText: string;
    chatId: string;
    chatName: string;
    /** True when the message has copyable text (body or caption). */
    canCopy: boolean;
    /** Toggle/replace this user's reaction; optimistic with best-effort revert. */
    applyReaction: (emoji: string) => Promise<void>;
    /** Stage the message as the composer's reply target. */
    replyToMessage: () => void;
    /** Copy body/caption to the clipboard. */
    copyText: () => void;
    /** Stage the message for the forward picker dialog. */
    forwardMessage: () => void;
    createNote: () => void;
    createTask: () => void;
    createReminder: () => void;
    pinMessage: () => Promise<void>;
    unpinMessage: () => Promise<void>;
    /** star=true stars, false unstars. */
    starMessage: (star: boolean) => Promise<void>;
    /** Delete for me / for everyone; marks the cache entity deleted on success. */
    deleteMessage: (forEveryone: boolean) => Promise<void>;
    /** Whether an engine serves each by-key write (its presence `commands`). */
    canReact: boolean;
    canForward: boolean;
    canPin: boolean;
    canStar: boolean;
    canDelete: boolean;
}

/**
 * All message-level actions, shared by the hover action bar and the full
 * right-click/chevron context menu so no tool-call code is duplicated.
 */
export function useMessageActions(message: any): MessageActions {
    const dispatch = useAppDispatch();
    const currentChat = useAppSelector(selectCurrentWaChat);
    const served = useAppSelector(selectServedCommands);

    const messageText = useMemo(() => {
        const raw = (message?.body || message?.caption || '').trim();
        return raw || mediaTypeLabel(message?.type) || '';
    }, [message?.body, message?.caption, message?.type]);

    const chatId: string = message?.chatId ?? currentChat?.chatId ?? '';
    const chatName: string = currentChat?.name || formatPhoneNumber(chatId) || '';
    const waKey: string | undefined = message?.waMessageKey?._serialized;
    const canCopy = !!((message?.body || message?.caption || '').trim())
        && message?.type !== 'revoked' && !message?.deleted;

    const applyReaction = useCallback(async (emoji: string) => {
        if (!message?._id || !waKey) return;

        const prevReactions = message.reactions;
        const { next, sendText } = toggleMyReaction(prevReactions, emoji);

        // Optimistic cache update (this user's reaction, fromMe: true).
        dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
            id: message._id,
            changes: { reactions: next },
        }));

        try {
            const res: any = await waReactToMessageThunkTool({ waMessageKey: waKey, text: sendText });
            if (res && res.success === false) throw new Error(res.error || 'react failed');
        } catch (err) {
            // Best-effort revert to the pre-click reaction list.
            dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                id: message._id,
                changes: { reactions: prevReactions ?? [] },
            }));
        }
    }, [dispatch, message, waKey]);

    const replyToMessage = useCallback(() => {
        dispatch(TeamInboxActions.setReplyingToMessage(message));
    }, [dispatch, message]);

    const copyText = useCallback(() => {
        const text = (message?.body || message?.caption || '').trim();
        if (!text) return;
        navigator.clipboard?.writeText(text).catch((err) => {
            console.warn('[team-inbox] Copy to clipboard failed', err);
        });
    }, [message?.body, message?.caption]);

    const forwardMessage = useCallback(() => {
        dispatch(TeamInboxActions.setForwardingMessage(message));
    }, [dispatch, message]);

    const createNote = useCallback(() => {
        if (!chatId) return;
        dispatch(createNoteFromMessageThunk({ chatId, messageText, messageId: message?._id }));
    }, [dispatch, chatId, messageText, message?._id]);

    const createTask = useCallback(() => {
        if (!chatId) return;
        dispatch(createTaskFromMessageThunk({ chatId, chatName, messageText, messageId: message?._id }));
    }, [dispatch, chatId, chatName, messageText, message?._id]);

    const createReminder = useCallback(() => {
        if (!chatId) return;
        dispatch(openEditor({
            reminder: undefined,
            context: { messageId: message?._id, messageText, chatId, chatDisplayName: chatName },
        }));
    }, [dispatch, chatId, chatName, messageText, message?._id]);

    const pinMessage = useCallback(async () => {
        if (!waKey) return;
        try {
            await waPinMessageTool({ waMessageKey: waKey });
        } catch (err) {
            console.warn('[team-inbox] Pin message failed', err);
        }
    }, [waKey]);

    const unpinMessage = useCallback(async () => {
        if (!waKey) return;
        try {
            await waUnpinMessageTool({ waMessageKey: waKey });
        } catch (err) {
            console.warn('[team-inbox] Unpin message failed', err);
        }
    }, [waKey]);

    const starMessage = useCallback(async (star: boolean) => {
        if (!waKey) return;
        try {
            const res: any = await waStarMessageTool({ waMessageKey: waKey, star });
            if (res && res.success === false) throw new Error(res.error || 'star failed');
            if (message?._id) {
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                    id: message._id,
                    changes: { star },
                }));
            }
        } catch (err) {
            console.warn('[team-inbox] Star message failed', err);
        }
    }, [dispatch, message?._id, waKey]);

    const deleteMessage = useCallback(async (forEveryone: boolean) => {
        if (!waKey) return;
        try {
            const res: any = await waDeleteMessageTool({ waMessageKey: waKey, forEveryone });
            if (res && res.success === false) throw new Error(res.error || 'delete failed');
            if (message?._id) {
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                    id: message._id,
                    changes: { deleted: true },
                }));
            }
        } catch (err) {
            console.warn('[team-inbox] Delete message failed', err);
        }
    }, [dispatch, message?._id, waKey]);

    return {
        messageText,
        chatId,
        chatName,
        canCopy,
        applyReaction,
        replyToMessage,
        copyText,
        forwardMessage,
        createNote,
        createTask,
        createReminder,
        pinMessage,
        unpinMessage,
        starMessage,
        deleteMessage,
        canReact: served.includes(WA_TOOL_COMMANDS.waReactToMessageThunkTool),
        canForward: served.includes(WA_TOOL_COMMANDS.waForwardMessageTool),
        canPin: served.includes(WA_TOOL_COMMANDS.waPinMessageTool),
        canStar: served.includes(WA_TOOL_COMMANDS.waStarMessageTool),
        canDelete: served.includes(WA_TOOL_COMMANDS.waDeleteMessageTool),
    };
}
